/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
*
* NVIDIA CORPORATION and its licensors retain all intellectual property
* and proprietary rights in and to this software, related documentation
* and any modifications thereto.  Any use, reproduction, disclosure or
* distribution of this software and related documentation without an express
* license agreement from NVIDIA CORPORATION is strictly prohibited.
*/

/** This file was automatically generated. DO NOT MODIFY IT BY HAND. */

/* eslint-disable @typescript-eslint/naming-convention */

export module NvcpldisplaypluginTypes {

/** This object was automatically generated by json-schema-to-typescript. DO NOT MODIFY IT BY HAND. */

export interface NvCplDisplayplugin {
    [k: string]: any;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GPUInfo".
 */
export interface GPUInfo {
    /**
     * unique identifier for Gpu
     */
    id?: number;
    /**
     * CUDA cores
     */
    cudaCore?: number;
    /**
     * true if GPU is NVIDIA
     */
    isNvidiaGpu?: boolean;
    /**
     * Long GPU Name
     */
    longGPUName?: string;
    /**
     * Core clock
     */
    coreClock?: number;
    /**
     * Memory Data Rate
     */
    memoryDataRate?: number;
    /**
     * Memory Interface
     */
    memoryInterface?: number;
    /**
     * Memory Bandwidth
     */
    memoryBandwidth?: number;
    ramType?: RamType;
    /**
     * Total available graphics memory
     */
    totalAvailableGraphicsMemory?: number;
    /**
     * Dedicated video memory
     */
    dedicatedVideoMemory?: number;
    /**
     * System video memory
     */
    systemVideoMemory?: number;
    /**
     * Shared system memory
     */
    sharedSystemMemory?: number;
    /**
     * Video BIOS version
     */
    videoBIOSVersion?: string;
    /**
     * IRQ
     */
    irq?: number;
    /**
     * Bus
     */
    bus?: number;
    busType?: BusType;
    /**
     * Device id
     */
    deviceId?: string;
    /**
     * Part Number
     */
    partNumber?: string;
    maxQTechnology?: MaxQType;
    maxGpuPower?: number;
    isResizableBar?: boolean;
}
/**
 * Surround resolution flags (NV_SURROUND_*)
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SurroundResolutionFlags".
 */
export interface SurroundResolutionFlags {
    /**
     * NV_SURROUND_CURRENT - currently applied surround resolution
     */
    isCurrent: boolean;
    /**
     * NV_SURROUND_DEFAULT - default 3 resolutions returned by display driver
     */
    isDefault: boolean;
    /**
     * NV_SURROUND_MOSAIC_RESOLUTION - grid wide resolution
     */
    isMosaicResolution: boolean;
    /**
     * NV_SURROUND_BEZEL_CORRECTED - bezel corrected resolution
     */
    isBezelCorrected: boolean;
    /**
     * NV_SURROUND_ACTIVE_RESOLUTION - resolution is visible to OS and user (and also to be shown in main surround resolution list)
     */
    isActiveResolution: boolean;
    /**
     * NV_SURROUND_RECOMMENDED - recommended (max) surround resolution
     */
    isRecommended: boolean;
}
/**
 * Single surround resolution entry (width, height, flags)
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SurroundResolution".
 */
export interface SurroundResolution {
    /**
     * Resolution width in pixels
     */
    width: number;
    /**
     * Resolution height in pixels
     */
    height: number;
    flags: SurroundResolutionFlags;
}
/**
 * List of surround resolutions (SURROUND_RESOLUTIONS struct)
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SurroundResolutions".
 */
export interface SurroundResolutions {
    resolution: SurroundResolution[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "MosaicGridDisplay".
 */
export interface MosaicGridDisplay {
    /**
     * DisplayID of the display
     */
    displayId: number;
    /**
     * (+overlap, -gap)
     */
    overlapX: number;
    /**
     * (+overlap, -gap)
     */
    overlapY: number;
    rotation: RotationType;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "MosaicGridDisplaySettings".
 */
export interface MosaicGridDisplaySettings {
    /**
     * per display width
     */
    width: number;
    /**
     * per display height
     */
    height: number;
    /**
     * bits per pixel
     */
    bpp: number;
    /**
     * display frequency
     */
    freq: number;
    /**
     * fractional refresh rate
     */
    rrx1k: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "MosaicGridTopo".
 */
export interface MosaicGridTopo {
    /**
     * Number of rows
     */
    rows: number;
    /**
     * Number of columns
     */
    columns: number;
    /**
     * Number of display details
     */
    displayCount: number;
    /**
     * When enabling and doing the modeset, do we switch to the bezel-corrected resolution
     */
    applyWithBezelCorrect: number;
    /**
     * Enable as immersive gaming instead of Mosaic SLI (for Quadro-boards only)
     */
    immersiveGaming: number;
    mosaicType: MosaicType;
    /**
     * to check whether it is a tile display or not for quadro
     */
    isAutoconfiguredMultiTileDisplay: boolean;
    /**
     * this overwrites the existing policy of selecting GPU topology of Mosaic/SLI Mosaic
     */
    useTopologyWithMaxGpus: boolean;
    /**
     * Displays are done as [(row * columns) + column]
     */
    displays: MosaicGridDisplay[];
    displaySettings: MosaicGridDisplaySettings;
}
/**
 * ECC Mode status of all the ECC Supported GPUs
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ECCModeStatus".
 */
export interface ECCModeStatus {
    /**
     * GPU Id
     */
    gpuId: number;
    /**
     * ECC Mode Status value
     */
    eccModeStatus: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SuperResolutionInfo".
 */
export interface SuperResolutionInfo {
    isSupported?: boolean;
    superResolutionValue?: SuperResolutionValue;
    superResolutionStatus?: SuperResolutionStatus;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "HDRInfo".
 */
export interface HDRInfo {
    isSupported: boolean;
    hdrValue: boolean;
    hdrPeakBrightness: number;
    hdrPeakBrightnessMax: number;
    hdrPeakBrightnessMin: number;
    hdrMiddleGray: number;
    hdrSaturation: number;
    hdrContrast: number;
    hdrErrorCodeInfo: HdrErrorCodeInfo;
    hdrStatus: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ResolutionFlags".
 */
export interface ResolutionFlags {
    isInterlaced: boolean;
    isHD: boolean;
    isSupported: boolean;
    isCustom: boolean;
    isCurrent: boolean;
    isNative: boolean;
    isRecommended: boolean;
    isCloneSupported: boolean;
    isHD_3D: boolean;
    isBezelCorrected: boolean;
    isSurround: boolean;
    isChecked: boolean;
    isBlindlyExposedHD: boolean;
    isCustomUnderscan: boolean;
    isDefaultUnderscan: boolean;
    isScalingFactor12000: boolean;
    isScalingFactor15000: boolean;
    isScalingFactor17777: boolean;
    isScalingFactor20000: boolean;
    isScalingFactor22500: boolean;
    isScalingFactor30000: boolean;
    isScalingFactor40000: boolean;
    isScalingFactor60000: boolean;
    isScalingFactor90000: boolean;
    isScalingFactor5000: boolean;
    isScalingFactor6667: boolean;
    isScalingFactor7500: boolean;
    isScalingFactor7693: boolean;
    isScalingFactor8500: boolean;
    isScalingFactor5883: boolean;
    isDLDSRFactor: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "Resolution".
 */
export interface Resolution {
    horizontal: number;
    vertical: number;
    flags: ResolutionFlags;
    tvFormat: TvFormat;
    refreshRate: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "Position".
 */
export interface Position {
    X: number;
    Y: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "HDCPStatus".
 */
export interface HDCPStatus {
    isUnavailable: boolean;
    isInvalidDisplayID: boolean;
    isInvalidDisplay: boolean;
    isInvalidDisplayMode: boolean;
    isInvalidGpu: boolean;
    isInvalidGpuMode: boolean;
    isAbortUntrust: boolean;
    isAbortLinkFailure: boolean;
    isAbortKsvLength: boolean;
    isAbortKsvSignature: boolean;
    isAbortSrmSignature: boolean;
    isAbortSrmRemoved: boolean;
    isAbortSrmRevoked: boolean;
    isAbortRepeaterNoReady: boolean;
    isAbortTopologyError: boolean;
    isAbortBadDisplay: boolean;
    isAbortDeviceRevoked: boolean;
    isAbortDeviceInvalid: boolean;
    isAbortHopLimitExceeded: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "HDCPLinkStatus".
 */
export interface HDCPLinkStatus {
    isRepeaterPresent: boolean;
    isDebuggerDetected: boolean;
    isHDCPOn: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "HDCPState".
 */
export interface HDCPState {
    hdcpStatus?: HDCPStatus;
    hdcpLinkStatus?: HDCPLinkStatus;
    isSupported: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "PulsarLowFpsRange".
 */
export interface PulsarLowFpsRange {
    /**
     * Minimum value of Pulsar low FPS slider
     */
    min: number;
    /**
     * Maximum value of Pulsar low FPS slider
     */
    max: number;
    /**
     * Current value of Pulsar low FPS slider
     */
    current: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "PulsarState".
 */
export interface PulsarState {
    /**
     * Whether G-SYNC Pulsar is enabled
     */
    enabled: boolean;
    lowFps: PulsarLowFpsRange;
    /**
     * Whether G-SYNC Pulsar can be used in the current runtime state
     */
    canUsePulsar?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "AmbientAdaptiveState".
 */
export interface AmbientAdaptiveState {
    /**
     * Whether ambient adaptive brightness is enabled
     */
    brightnessEnabled: boolean;
    /**
     * Whether ambient adaptive color is enabled
     */
    colorEnabled: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GsyncState".
 */
export interface GsyncState {
    gsyncType: GSyncCapabilities;
    enabled: boolean;
    pulsar?: PulsarState;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "DisplayPhysicalInformation".
 */
export interface DisplayPhysicalInformation {
    name: string;
    connectorType: ConnectorType;
    deviceType: DeviceType;
    nvDisplayId: number;
    gpuId: number;
    isTileDisplay?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "DisplaylogicalInformation".
 */
export interface DisplaylogicalInformation {
    currentResolution: Resolution;
    position: Position;
    index: number;
    gpuIndex: number;
    identifier: number;
    hdcpState: HDCPState;
    gsyncState: GsyncState;
    rotationState: RotationType;
    isAdvanceColorSettingsSupported?: boolean;
    isClonePrimary: boolean;
    logicalDisplayType: LogicalDisplayType;
    nvDisplayId: number;
    gpuId: number;
    isHideModeEnabled?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "Surface".
 */
export interface Surface {
    id: number;
    displays: DisplaylogicalInformation[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "Topology".
 */
export interface Topology {
    surfaces: Surface[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "DisplaysState".
 */
export interface DisplaysState {
    topology: Topology;
    inactiveDisplays: DisplayPhysicalInformation[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "Display".
 */
export interface Display {
    currentResolution?: Resolution;
    name: string;
    /**
     * Friendly name of the wireless display dongle, when available.
     */
    dongleName?: string;
    /**
     * Indicates whether the display uses a wireless connection.
     */
    isWirelessDisplay?: boolean;
    index: number;
    identifier: number;
    desktopColorDepth: DesktopColorDepth;
    outPutColorFormat: OutPutColorFormat;
    hdcpState: HDCPState;
    gsyncState: GsyncState;
    connectorType: ConnectorType;
    deviceType: DeviceType;
    rotationState: RotationType;
    isAdvanceColorSettingsSupported?: boolean;
    desktopColorDepthCurrentIndex?: number;
    isHideModeEnabled?: boolean;
    isClonePrimary: boolean;
    isTileDisplay?: boolean;
    logicalDisplayType: LogicalDisplayType;
    nvDisplayId: number;
    cloneGroupId: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GPU".
 */
export interface GPU {
    name: string;
    index: number;
    id?: number;
    displays: Display[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "PhysicalGPU".
 */
export interface PhysicalGPU {
    name: string;
    id: number;
    displays: DisplayPhysicalInformation[];
}
/**
 * An application currently running on a GPU.
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GpuActivityApp".
 */
export interface GpuActivityApp {
    /**
     * Installed product name, or the executable name when no product name is available.
     */
    friendlyName: string;
    /**
     * Application icon as a base64 encoded PNG. Empty when no icon could be produced.
     */
    iconBase64: string;
}
/**
 * Represents a valid color combination for Advanced Color Settings.
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "AdvancedColorCombination".
 */
export interface AdvancedColorCombination {
    outputColorFormat: OutPutColorFormat;
    dynamicRange: DynamicRange;
    outputColorDepth: OutputColorDepth;
    desktopColorDepth: DesktopColorDepth;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ChannelComponents".
 */
export interface ChannelComponents {
    red: number;
    green: number;
    blue: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ColorCombinationInfo".
 */
export interface ColorCombinationInfo {
    contrastValue: ChannelComponents;
    contrastMinValue: ChannelComponents;
    contrastMaxValue: ChannelComponents;
    brightnessValue: ChannelComponents;
    brightnessMinValue: ChannelComponents;
    brightnessMaxValue: ChannelComponents;
    gammaValue: ChannelComponents;
    gammaMinValue: ChannelComponents;
    gammaMaxValue: ChannelComponents;
}
/**
 * Custom timing details
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "CustomTimingOperations".
 */
export interface CustomTimingOperations {
    TestSet?: boolean;
    TestRevert?: boolean;
    TestSave?: boolean;
    TestCalculate?: boolean;
    TestForEdid?: boolean;
    TestForOutputType?: boolean;
    DeleteByValue?: boolean;
    DeleteByIndex?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ScalingSettings".
 */
export interface ScalingSettings {
    scalingMethod: ScalingMethod;
    scalingMode: ScalingMode;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ScalingSettingsPossibleValue".
 */
export interface ScalingSettingsPossibleValue {
    scalingMethod: ScalingMethod;
    supportedScalingModeList?: ScalingMode[];
}
/**
 * Custom timing details
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "CustomTiming".
 */
export interface CustomTiming {
    logicalX: number;
    logicalY: number;
    frontEndX: number;
    frontEndY: number;
    backEndX: number;
    backEndY: number;
    scalingType: ScalingType;
    isInterlaced: boolean;
    requestedRefresh: number;
    actualRefresh: number;
    timingFormula: TimingFormula;
    horizontalSyncWidth: number;
    horizontalFP: number;
    horizontalTotal: number;
    isHorizontalPolarityPositive: boolean;
    verticalSyncWidth: number;
    verticalFP: number;
    verticalTotal: number;
    isVerticalPolarityPositive: boolean;
    actualPixelClock: number;
    isCeTiming: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "DisplayLuid".
 */
export interface DisplayLuid {
    hiPart?: number;
    lowPart?: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "Rectangle".
 */
export interface Rectangle {
    top: number;
    left: number;
    right: number;
    bottom: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ScreenSize".
 */
export interface ScreenSize {
    horizontal: number;
    vertical: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ScalingPreviewInfo".
 */
export interface ScalingPreviewInfo {
    scanoutRectangle: Rectangle;
    rasterRectangle: Rectangle;
    screenSize: ScreenSize;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "MosaicDisplayConfig".
 */
export interface MosaicDisplayConfig {
    displayCount: number;
    displayIds: number[];
    errorFlags: number[];
    displaySettingsCount: number;
    displaySettings: MosaicGridDisplaySettings[];
    mosaicType: MosaicType;
    rows: number;
    columns: number;
    isPlusN: boolean;
    useTopologyWithMaxGpus: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SpanIdentifier".
 */
export interface SpanIdentifier {
    displayId?: number;
    identifier?: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "HotkeyState".
 */
export interface HotkeyState {
    hotkeyType: HotkeyType;
    /**
     * Hotkey value in WM_GETHOTKEY format
     */
    hotkeyValue: number;
    /**
     * Whether the hotkey is enabled or not
     */
    isEnabled: boolean;
}
/**
 * GPU memory allocation information. Allocation values are in MiB.
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GpuMemoryAllocationInfo".
 */
export interface GpuMemoryAllocationInfo {
    /**
     * Requested GPU memory allocation stored in firmware, in MiB. Equals activeAllocation when no restart is pending.
     */
    currentAllocation: number;
    /**
     * GPU memory allocation in effect in firmware, in MiB.
     */
    activeAllocation: number;
    /**
     * Firmware-provided minimum GPU memory allocation in MiB.
     */
    minAllocation: number;
    /**
     * Maximum GPU memory allocation from the NDUP SKU fallback in MiB.
     */
    maxAllocation: number;
    /**
     * Default GPU memory allocation from the NDUP SKU fallback in MiB.
     */
    defaultAllocation: number;
    /**
     * Firmware-provided GPU memory allocation step size in MiB.
     */
    stepAllocation: number;
    /**
     * Whether the requested allocation differs from the packaged NDUP default for this system-memory SKU.
     */
    restoreAvailable: boolean;
    /**
     * Whether the packaged NDUP default is requested and will become active after restart.
     */
    restorePending: boolean;
    /**
     * Total physically installed system memory in GB.
     */
    totalSystemMemoryGB: number;
    /**
     * Whether Intelligent Carveout is supported on this system.
     */
    intelligentCarveoutSupported: boolean;
    /**
     * Requested Intelligent Carveout state. Equals activeIntelligentCarveoutEnabled unless a pending write has been made.
     */
    currentIntelligentCarveoutEnabled: boolean;
    /**
     * Effective Intelligent Carveout runtime state.
     */
    activeIntelligentCarveoutEnabled: boolean;
}
/**
 * GPU utilization preference information returned by UXD.
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GpuUtilizationPreferenceInfo".
 */
export interface GpuUtilizationPreferenceInfo {
    isSupported: boolean;
    /**
     * Exact native YieldPct readback. Non-preset values are preserved.
     */
    currentYieldPct: number;
    /**
     * Native default YieldPct.
     */
    defaultYieldPct: number;
    /**
     * Native minimum YieldPct.
     */
    minYieldPct: number;
    /**
     * Native maximum YieldPct.
     */
    maxYieldPct: number;
    /**
     * Native YieldPct increment.
     */
    stepYieldPct: number;
    /**
     * Native preference values reported as supported by UXD.
     */
    supportedPreferences: GpuUtilizationPreferenceValue[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDirect3DfeatureLevelParameters".
 */
export interface GetDirect3DfeatureLevelParameters {
    /**
     * Index of GPU for which information is required.
     */
    gpuIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDirect3DfeatureLevelReturn".
 */
export interface GetDirect3DfeatureLevelReturn {
    featureLevel?: string;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetGPUInformationParameters".
 */
export interface GetGPUInformationParameters {
    /**
     * Index of GPU for which information is required.
     */
    gpuIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetGPUInformationReturn".
 */
export interface GetGPUInformationReturn {
    gpu: GPUInfo;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "IsECCModeSupportedReturn".
 */
export interface IsECCModeSupportedReturn {
    isSupported?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetECCModeStatusReturn".
 */
export interface GetECCModeStatusReturn {
    eccModeStatus: ECCModeStatus[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetECCModeStatusParameters".
 */
export interface SetECCModeStatusParameters {
    eccModeStatus: ECCModeStatus[];
    /**
     * ECC Mode Status value
     */
    isRestartRequired: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetECCModeStatusReturn".
 */
export interface SetECCModeStatusReturn {
    eccModeResult?: EccModeResult;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetSuperResolutionInfoReturn".
 */
export interface GetSuperResolutionInfoReturn {
    SuperResolutionInfoValue?: SuperResolutionInfo;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetSuperResolutionCurrentStatusReturn".
 */
export interface GetSuperResolutionCurrentStatusReturn {
    superResStatus?: SuperResolutionStatus;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetSuperResolutionValueParameters".
 */
export interface SetSuperResolutionValueParameters {
    superResolutionValue?: SuperResolutionValue;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetSuperResolutionGpuUtilizationReturn".
 */
export interface GetSuperResolutionGpuUtilizationReturn {
    superResolutionGpuUtilization?: SuperResolutionGpuUtilization;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetSuperResolutionGpuUtilizationParameters".
 */
export interface SetSuperResolutionGpuUtilizationParameters {
    superResolutionGpuUtilization?: SuperResolutionGpuUtilization;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetSuperResolutionIndicatorStatusReturn".
 */
export interface GetSuperResolutionIndicatorStatusReturn {
    superResolutionIndicator?: SuperResolutionIndicator;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetRTXVideoFlagsReturn".
 */
export interface GetRTXVideoFlagsReturn {
    superResolutionIndicator?: SuperResolutionIndicator;
    disableVSROnGameLaunch?: DisableVsrOnGameLaunch;
    hdrIndicator?: HdrIndicator;
    disableHDROnGameLaunch?: DisableHdrOnGameLaunch;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetRTXVSRFlagsParameters".
 */
export interface SetRTXVSRFlagsParameters {
    superResolutionIndicator?: SuperResolutionIndicator;
    disableVSROnGameLaunch?: DisableVsrOnGameLaunch;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetRTXHDRFlagsParameters".
 */
export interface SetRTXHDRFlagsParameters {
    hdrIndicator?: HdrIndicator;
    disableHDROnGameLaunch?: DisableHdrOnGameLaunch;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetSuperResolutionIndicatorStatusParameters".
 */
export interface SetSuperResolutionIndicatorStatusParameters {
    superResolutionIndicator?: SuperResolutionIndicator;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetHDRIndicatorStatusReturn".
 */
export interface GetHDRIndicatorStatusReturn {
    hdrIndicator?: HdrIndicator;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetHDRIndicatorStatusParameters".
 */
export interface SetHDRIndicatorStatusParameters {
    hdrIndicator?: HdrIndicator;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDisableVSROnGameLaunchStatusReturn".
 */
export interface GetDisableVSROnGameLaunchStatusReturn {
    disableVSROnGameLaunch?: DisableVsrOnGameLaunch;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetDisableVSROnGameLaunchStatusParameters".
 */
export interface SetDisableVSROnGameLaunchStatusParameters {
    disableVSROnGameLaunch?: DisableVsrOnGameLaunch;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDisableHDROnGameLaunchStatusReturn".
 */
export interface GetDisableHDROnGameLaunchStatusReturn {
    disableHDROnGameLaunch?: DisableHdrOnGameLaunch;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetDisableHDROnGameLaunchStatusParameters".
 */
export interface SetDisableHDROnGameLaunchStatusParameters {
    disableHDROnGameLaunch?: DisableHdrOnGameLaunch;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetHDRInfoReturn".
 */
export interface GetHDRInfoReturn {
    HDRInfoValue?: HDRInfo;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetHDRCurrentStatusReturn".
 */
export interface GetHDRCurrentStatusReturn {
    hdrStatus?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetHDRValueParameters".
 */
export interface SetHDRValueParameters {
    hdrValue?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetHDRInfoParameters".
 */
export interface SetHDRInfoParameters {
    HDRInfoValue?: HDRInfo;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetGpuCountReturn".
 */
export interface GetGpuCountReturn {
    gpuCount?: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "LaunchApplicationParameters".
 */
export interface LaunchApplicationParameters {
    clsId: string;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "IsControlPanelClientInstalledReturn".
 */
export interface IsControlPanelClientInstalledReturn {
    isClientInstalled?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDisplaysStateReturn".
 */
export interface GetDisplaysStateReturn {
    displaysState: DisplaysState;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetDisplaysStateParameters".
 */
export interface SetDisplaysStateParameters {
    topology: Topology;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetDisplaysStateReturn".
 */
export interface SetDisplaysStateReturn {
    isValidationSuccess: boolean;
    displaysState?: DisplaysState;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDebugModeStatusReturn".
 */
export interface GetDebugModeStatusReturn {
    debugModeStatus: boolean;
    isSupported?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetDebugModeStatusParameters".
 */
export interface SetDebugModeStatusParameters {
    debugModeStatus?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "IsDebugModeSupportedReturn".
 */
export interface IsDebugModeSupportedReturn {
    /**
     * Is Debug Mode Supported on this system or not.
     */
    isSupported?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDisplayResolutionListParameters".
 */
export interface GetDisplayResolutionListParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDisplayResolutionListReturn".
 */
export interface GetDisplayResolutionListReturn {
    resolutions: Resolution[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetGpuAndDisplayInformationReturn".
 */
export interface GetGpuAndDisplayInformationReturn {
    gpus: GPU[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetGpuActivityInformationParameters".
 */
export interface GetGpuActivityInformationParameters {
    gpuIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetGpuActivityInformationReturn".
 */
export interface GetGpuActivityInformationReturn {
    /**
     * Empty when the GPU is idle.
     */
    apps: GpuActivityApp[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetIGpuAndDisplayInformationReturn".
 */
export interface GetIGpuAndDisplayInformationReturn {
    gpus: GPU[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetGpuAndDisplaysPhysicalInformationReturn".
 */
export interface GetGpuAndDisplaysPhysicalInformationReturn {
    physicalGpus: PhysicalGPU[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "RestoreDisplaySettingsParameters".
 */
export interface RestoreDisplaySettingsParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetResolutionParameters".
 */
export interface SetResolutionParameters {
    gpuIndex: number;
    displayIndex: number;
    resolution: Resolution;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetRotationParameters".
 */
export interface SetRotationParameters {
    gpuIndex: number;
    displayIndex: number;
    rotation: RotationType;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "IdentifyDisplaysParameters".
 */
export interface IdentifyDisplaysParameters {
    isAllDisplays?: boolean;
    /**
     * Show span identifiers.
     * The identifiers are calculated from a different algorithm than the usual monitor identifiers.
     * When this is true we use nvcpl's surround dialog's logic,
     * otherwise we use the sumd page identify logic.
     * This will work irrespective of surround being enabled or disabled.
     */
    showSpanIdentifiers?: boolean;
    /**
     * Hide any identifiers currently shown
     */
    hideIdentifiers?: boolean;
    monitorIdentifiers: number[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetSystemCapabilitiesReturn".
 */
export interface GetSystemCapabilitiesReturn {
    isAdvancedOptimus: boolean;
    isMsHybridSystem: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetGlobalGsyncStateReturn".
 */
export interface GetGlobalGsyncStateReturn {
    globalGsyncState: GlobalGsyncState;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetGlobalGsyncStateParameters".
 */
export interface SetGlobalGsyncStateParameters {
    globalGsyncState: GlobalGsyncState;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetGsyncIndicatorReturn".
 */
export interface GetGsyncIndicatorReturn {
    isEnabled: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetGsyncIndicatorParameters".
 */
export interface SetGsyncIndicatorParameters {
    isEnabled: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "RestoreGlobalGsyncStateReturn".
 */
export interface RestoreGlobalGsyncStateReturn {
    globalGsyncState: GlobalGsyncState;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDisplayGsyncStateParameters".
 */
export interface GetDisplayGsyncStateParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDisplayGsyncStateReturn".
 */
export interface GetDisplayGsyncStateReturn {
    gsyncState: GsyncState;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetDisplayGsyncStateParameters".
 */
export interface SetDisplayGsyncStateParameters {
    gpuIndex: number;
    displayIndex: number;
    enabled: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetDisplayPulsarInfoParameters".
 */
export interface SetDisplayPulsarInfoParameters {
    gpuIndex: number;
    displayIndex: number;
    pulsar: PulsarState;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDisplayAmbientAdaptiveInfoParameters".
 */
export interface GetDisplayAmbientAdaptiveInfoParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDisplayAmbientAdaptiveInfoReturn".
 */
export interface GetDisplayAmbientAdaptiveInfoReturn {
    /**
     * Whether Ambient Adaptive feature is supported
     */
    isSupported: boolean;
    ambientAdaptive?: AmbientAdaptiveState;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetDisplayAmbientAdaptiveBrightnessInfoParameters".
 */
export interface SetDisplayAmbientAdaptiveBrightnessInfoParameters {
    gpuIndex: number;
    displayIndex: number;
    /**
     * Whether ambient adaptive brightness is enabled
     */
    brightnessEnabled: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetDisplayAmbientAdaptiveColorInfoParameters".
 */
export interface SetDisplayAmbientAdaptiveColorInfoParameters {
    gpuIndex: number;
    displayIndex: number;
    /**
     * Whether ambient adaptive color is enabled
     */
    colorEnabled: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetIsColorSettingsOsControlledParameters".
 */
export interface GetIsColorSettingsOsControlledParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetIsColorSettingsOsControlledReturn".
 */
export interface GetIsColorSettingsOsControlledReturn {
    isColorSettingsOsControlled: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetIsColorSettingsOsControlledParameters".
 */
export interface SetIsColorSettingsOsControlledParameters {
    gpuIndex: number;
    displayIndex: number;
    isColorSettingsOsControlled: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetColorEnhancementInfoParameters".
 */
export interface GetColorEnhancementInfoParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetColorEnhancementInfoReturn".
 */
export interface GetColorEnhancementInfoReturn {
    dynamicRangeCurrentValue: DynamicRange;
    dynamicRangePossibleValues: DynamicRange[];
    outputColorDepthCurrentValue: OutputColorDepth;
    outputColorDepthPossibleValues: OutputColorDepth[];
    outputColorFormatCurrentValue: OutPutColorFormat;
    outputColorFormatPossibleValues: OutPutColorFormat[];
    desktopColorDepthCurrentValue: DesktopColorDepth;
    desktopColorDepthPossibleValues: DesktopColorDepth[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetAdvancedColorCombinationInfoParameters".
 */
export interface GetAdvancedColorCombinationInfoParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetAdvancedColorCombinationInfoReturn".
 */
export interface GetAdvancedColorCombinationInfoReturn {
    currentColorCombination: AdvancedColorCombination;
    possibleColorCombinations: AdvancedColorCombination[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetAdvancedColorCombinationInfoParameters".
 */
export interface SetAdvancedColorCombinationInfoParameters {
    gpuIndex: number;
    displayIndex: number;
    colorCombination: AdvancedColorCombination;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetDynamicRangeParameters".
 */
export interface SetDynamicRangeParameters {
    gpuIndex: number;
    displayIndex: number;
    dynamicRange: DynamicRange;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetOutputColorFormatParameters".
 */
export interface SetOutputColorFormatParameters {
    gpuIndex: number;
    displayIndex: number;
    outputColorFormat: OutPutColorFormat;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetDesktopColorDepthParameters".
 */
export interface SetDesktopColorDepthParameters {
    gpuIndex: number;
    displayIndex: number;
    desktopColorDepth: DesktopColorDepth;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetOutputColorDepthParameters".
 */
export interface SetOutputColorDepthParameters {
    gpuIndex: number;
    displayIndex: number;
    outputColorDepth: OutputColorDepth;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDesktopColorCombinationInfoParameters".
 */
export interface GetDesktopColorCombinationInfoParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDesktopColorCombinationInfoReturn".
 */
export interface GetDesktopColorCombinationInfoReturn {
    colorCombinationInfo: ColorCombinationInfo;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDigitalVibranceInfoParameters".
 */
export interface GetDigitalVibranceInfoParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDigitalVibranceInfoReturn".
 */
export interface GetDigitalVibranceInfoReturn {
    digitalVibranceValue: number;
    digitalVibranceMin: number;
    digitalVibranceMax: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetDigitalVibranceParameters".
 */
export interface SetDigitalVibranceParameters {
    gpuIndex: number;
    displayIndex: number;
    digitalVibranceValue: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetHueInfoParameters".
 */
export interface GetHueInfoParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetHueInfoReturn".
 */
export interface GetHueInfoReturn {
    hueValue: number;
    hueMin: number;
    hueMax: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetHueParameters".
 */
export interface SetHueParameters {
    gpuIndex: number;
    displayIndex: number;
    hueValue: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetColorCombinationInfoParameters".
 */
export interface SetColorCombinationInfoParameters {
    gpuIndex: number;
    displayIndex: number;
    brightnessInfo: ChannelComponents;
    contrastInfo: ChannelComponents;
    gammaInfo: ChannelComponents;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetColorAccuracyModeParameters".
 */
export interface SetColorAccuracyModeParameters {
    gpuIndex: number;
    displayIndex: number;
    colorAccuracyMode: ColorAccuracyMode;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "RestoreColorAccuracyModeReturn".
 */
export interface RestoreColorAccuracyModeReturn {
    colorAccuracyMode: ColorAccuracyMode;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetColorAccuracyModeParameters".
 */
export interface GetColorAccuracyModeParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetColorAccuracyModeReturn".
 */
export interface GetColorAccuracyModeReturn {
    colorAccuracyMode: ColorAccuracyMode;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetScalingSettingsParameters".
 */
export interface GetScalingSettingsParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetScalingSettingsReturn".
 */
export interface GetScalingSettingsReturn {
    scalingSettingsCurrent: ScalingSettings;
    scalingSettingsPossibleValues: ScalingSettings[];
    isOverrideScalingEnabled: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetScalingSettingsParameters".
 */
export interface SetScalingSettingsParameters {
    gpuIndex: number;
    displayIndex: number;
    scalingSettings: ScalingSettings;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetScalingOverrideParameters".
 */
export interface SetScalingOverrideParameters {
    gpuIndex: number;
    displayIndex: number;
    isOverrideScalingEnabled: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "FetchCustomTimingParameters".
 */
export interface FetchCustomTimingParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "FetchCustomTimingReturn".
 */
export interface FetchCustomTimingReturn {
    /**
     * Array of custom timings
     */
    customTimingsList: number[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "PerformActionOnCustomResolutionLocalCacheParameters".
 */
export interface PerformActionOnCustomResolutionLocalCacheParameters {
    customDisplayResolution: Resolution;
    gpuIndex: number;
    displayIndex: number;
    customResolutionLocalCacheActions: CustomResolutionLocalCacheActions;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "PerformActionOnCustomResolutionLocalCacheReturn".
 */
export interface PerformActionOnCustomResolutionLocalCacheReturn {
    success: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetContentTypeInfoParameters".
 */
export interface GetContentTypeInfoParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetContentTypeInfoReturn".
 */
export interface GetContentTypeInfoReturn {
    contentType?: ContentType;
    /**
     * Array of supported content types
     */
    contentTypeSupportedList: ContentType[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetContentTypeParameters".
 */
export interface SetContentTypeParameters {
    gpuIndex: number;
    displayIndex: number;
    contentType: ContentType;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetIsUpscalingFactorsEnabledReturn".
 */
export interface GetIsUpscalingFactorsEnabledReturn {
    enabled: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDisplayLuidParameters".
 */
export interface GetDisplayLuidParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDisplayLuidReturn".
 */
export interface GetDisplayLuidReturn {
    displayLuid: DisplayLuid;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "RestoreDisplayScalingParameters".
 */
export interface RestoreDisplayScalingParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "RestoreDisplayColorSettingsParameters".
 */
export interface RestoreDisplayColorSettingsParameters {
    gpuIndex: number;
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetIsInfoFrameEnabledReturn".
 */
export interface GetIsInfoFrameEnabledReturn {
    enabled: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetScalingPreviewInfoParameters".
 */
export interface GetScalingPreviewInfoParameters {
    gpuIndex: number;
    displayIndex: number;
    resolution: Resolution;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetScalingPreviewInfoReturn".
 */
export interface GetScalingPreviewInfoReturn {
    scalingPreviewInfo: ScalingPreviewInfo;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetSurroundOrMosaicConfigParameters".
 */
export interface GetSurroundOrMosaicConfigParameters {
    mosaicType: MosaicType;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetSurroundOrMosaicConfigReturn".
 */
export interface GetSurroundOrMosaicConfigReturn {
    MosaicGridTopoValue: MosaicGridTopo;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetSurroundOrMosaicConfigParameters".
 */
export interface SetSurroundOrMosaicConfigParameters {
    MosaicGridTopoValue: MosaicGridTopo;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetSurroundResolutionReturn".
 */
export interface GetSurroundResolutionReturn {
    surroundResolutions: SurroundResolutions;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetSurroundResolutionParameters".
 */
export interface SetSurroundResolutionParameters {
    surroundResolutions: SurroundResolutions;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDDSStateReturn".
 */
export interface GetDDSStateReturn {
    bIsSupported: boolean;
    /**
     * system is in auto mode or not
     */
    bIsAutomatic: boolean;
    MuxState: MuxState;
    kmdResponse: KmdResponse;
    srType: SrType;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetDDSStateParameters".
 */
export interface SetDDSStateParameters {
    /**
     * system is in auto mode or not
     */
    bIsAutomatic: boolean;
    MuxState: MuxState;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "DDSBlockingAppPresentReturn".
 */
export interface DDSBlockingAppPresentReturn {
    /**
     * at least 1 blocking app present
     */
    bBlockingAppPresent?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ACPowerConnectedReturn".
 */
export interface ACPowerConnectedReturn {
    bAcConnected?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetUIStateParameters".
 */
export interface SetUIStateParameters {
    uiElem: UiElemType;
    state: UiElemState;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetUIStateParameters".
 */
export interface GetUIStateParameters {
    uiElem: UiElemType;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetUIStateReturn".
 */
export interface GetUIStateReturn {
    state: UiElemState;
    /**
     * Whether the UI element is in a supported state
     */
    isSupported?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetDDSBlockingAppsReturn".
 */
export interface GetDDSBlockingAppsReturn {
    /**
     * Array of application names that are blocking DDS
     */
    appList: string[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetMosaicDisplaySettingsParameters".
 */
export interface GetMosaicDisplaySettingsParameters {
    mosaicDisplayConfigIn: MosaicDisplayConfig;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetMosaicDisplaySettingsReturn".
 */
export interface GetMosaicDisplaySettingsReturn {
    mosaicDisplayConfigOut: MosaicDisplayConfig;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetSpanIdentifiersReturn".
 */
export interface GetSpanIdentifiersReturn {
    spanIdentifiers: SpanIdentifier[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetHotkeyStatesReturn".
 */
export interface GetHotkeyStatesReturn {
    /**
     * Array of hotkey states
     */
    hotkeyStates: HotkeyState[];
    /**
     * Global hotkey state. Individual hotkeys will work only if this is enabled.
     */
    globalHotKeysEnabled?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetHotkeyStatesParameters".
 */
export interface SetHotkeyStatesParameters {
    /**
     * Array of hotkey states to set.
     */
    hotkeyStates: HotkeyState[];
    /**
     * Global hotkey state. Individual hotkeys will work only if this is enabled.
     */
    globalHotKeysEnabled?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "PerfCountRestrictionInfoReturn".
 */
export interface PerfCountRestrictionInfoReturn {
    isCurrentUserAdmin: boolean;
    isNonAdminPerfCounterAccessAllowed: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetIsNonAdminPerfCounterAccessAllowedParameters".
 */
export interface SetIsNonAdminPerfCounterAccessAllowedParameters {
    isNonAdminPerfCounterAccessAllowed?: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetSpanBezelOverlapLimitsParameters".
 */
export interface GetSpanBezelOverlapLimitsParameters {
    displaySettings: MosaicGridDisplaySettings;
    /**
     * Number of rows
     */
    rows: number;
    /**
     * Number of columns
     */
    columns: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetSpanBezelOverlapLimitsReturn".
 */
export interface GetSpanBezelOverlapLimitsReturn {
    /**
     * Minimum horizontal bezel overlap
     */
    overlapXMin: number;
    /**
     * Maximum horizontal bezel overlap
     */
    overlapXMax: number;
    /**
     * Minimum vertical bezel overlap
     */
    overlapYMin: number;
    /**
     * Maximum vertical bezel overlap
     */
    overlapYMax: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ShowBezelPreviewImageParameters".
 */
export interface ShowBezelPreviewImageParameters {
    /**
     * Number of rows in the mosaic grid
     */
    rows: number;
    /**
     * Number of columns in the mosaic grid
     */
    columns: number;
    /**
     * Array of mosaic grid display configurations
     */
    displays: MosaicGridDisplay[];
    /**
     * Nested array where each sub-array contains a pair of display IDs. For 1xn, nx1 surround the array will only have a single pair. for 2x2 surround the array will have 2 pairs.
     */
    displayIdPairs?: number[][];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetCustomResolutionsParameters".
 */
export interface GetCustomResolutionsParameters {
    /**
     * Index of the GPU (0-based)
     */
    gpuIndex: number;
    /**
     * Index of the display (0-based)
     */
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetCustomResolutionsReturn".
 */
export interface GetCustomResolutionsReturn {
    /**
     * Array of all custom resolutions
     */
    customResolutions: CustomTiming[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "PerformCustomResolutionOperationParameters".
 */
export interface PerformCustomResolutionOperationParameters {
    /**
     * Index of the GPU (0-based)
     */
    gpuIndex: number;
    /**
     * Index of the display (0-based)
     */
    displayIndex: number;
    operation: CustomResolutionOperation;
    customTiming?: CustomTiming;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "DeleteCustomResolutionParameters".
 */
export interface DeleteCustomResolutionParameters {
    /**
     * Index of the GPU (0-based)
     */
    gpuIndex: number;
    /**
     * Index of the display (0-based)
     */
    displayIndex: number;
    customTiming: CustomTiming;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetSelectedCustomResolutionParameters".
 */
export interface GetSelectedCustomResolutionParameters {
    /**
     * Index of the GPU (0-based)
     */
    gpuIndex: number;
    /**
     * Index of the display (0-based)
     */
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetSelectedCustomResolutionReturn".
 */
export interface GetSelectedCustomResolutionReturn {
    selectedResolutions: Resolution[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "CalculateCustomTimingParameters".
 */
export interface CalculateCustomTimingParameters {
    /**
     * Index of the GPU (0-based)
     */
    gpuIndex: number;
    /**
     * Index of the display (0-based)
     */
    displayIndex: number;
    customTiming: CustomTiming;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "CalculateCustomTimingReturn".
 */
export interface CalculateCustomTimingReturn {
    customTiming: CustomTiming;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SaveSelectedCustomResolutionParameters".
 */
export interface SaveSelectedCustomResolutionParameters {
    /**
     * Index of the GPU (0-based)
     */
    gpuIndex: number;
    /**
     * Index of the display (0-based)
     */
    displayIndex: number;
    selectedResolutions: Resolution[];
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "IsLegalDisclaimerAcceptedParameters".
 */
export interface IsLegalDisclaimerAcceptedParameters {}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "IsLegalDisclaimerAcceptedReturn".
 */
export interface IsLegalDisclaimerAcceptedReturn {
    /**
     * True if the legal disclaimer has been accepted (registry value is 1), false otherwise
     */
    isAccepted: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetLegalDisclaimerAcceptedParameters".
 */
export interface SetLegalDisclaimerAcceptedParameters {}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetEnableHiddenResolutionsParameters".
 */
export interface GetEnableHiddenResolutionsParameters {
    /**
     * Index of the GPU (0-based)
     */
    gpuIndex: number;
    /**
     * Index of the display (0-based)
     */
    displayIndex: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetEnableHiddenResolutionsReturn".
 */
export interface GetEnableHiddenResolutionsReturn {
    /**
     * True if hidden resolutions are enabled (checkbox checked), false if disabled (checkbox unchecked)
     */
    isEnabled: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetEnableHiddenResolutionsParameters".
 */
export interface SetEnableHiddenResolutionsParameters {
    /**
     * Index of the GPU (0-based)
     */
    gpuIndex: number;
    /**
     * Index of the display (0-based)
     */
    displayIndex: number;
    /**
     * True to enable hidden resolutions (check checkbox), false to disable (uncheck)
     */
    isEnabled: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetContextMenuVisibilityParameters".
 */
export interface GetContextMenuVisibilityParameters {}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetContextMenuVisibilityReturn".
 */
export interface GetContextMenuVisibilityReturn {
    /**
     * True if the context menu is visible (registry value is 1), false otherwise
     */
    isVisible: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetContextMenuVisibilityParameters".
 */
export interface SetContextMenuVisibilityParameters {
    /**
     * True to show the context menu (sets registry value to 1), false to hide (sets registry value to 0)
     */
    isVisible: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "IsGpuMemoryAllocationSupportedReturn".
 */
export interface IsGpuMemoryAllocationSupportedReturn {
    isSupported: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetGpuMemoryAllocationInfoReturn".
 */
export interface GetGpuMemoryAllocationInfoReturn {
    gpuMemoryAllocationInfo: GpuMemoryAllocationInfo;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetGpuMemoryAllocationParameters".
 */
export interface SetGpuMemoryAllocationParameters {
    /**
     * Requested GPU memory allocation in MiB.
     */
    allocation: number;
    restartSystemNow: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetGpuMemoryAllocationReturn".
 */
export interface SetGpuMemoryAllocationReturn {
    writeSucceeded: boolean;
    restartInitiated: boolean;
    errorCode: ErrorCode;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "RestoreGpuMemoryAllocationParameters".
 */
export interface RestoreGpuMemoryAllocationParameters {
    /**
     * Whether to restart the system immediately after restoring the packaged NDUP default allocation.
     */
    restartSystemNow: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "RestoreGpuMemoryAllocationReturn".
 */
export interface RestoreGpuMemoryAllocationReturn {
    writeSucceeded: boolean;
    restartInitiated: boolean;
    errorCode: ErrorCode1;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetIntelligentCarveoutParameters".
 */
export interface SetIntelligentCarveoutParameters {
    enabled: boolean;
    restartSystemNow: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetIntelligentCarveoutReturn".
 */
export interface SetIntelligentCarveoutReturn {
    writeSucceeded: boolean;
    restartInitiated: boolean;
    errorCode: ErrorCode2;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "IsGpuUtilizationPreferenceSupportedReturn".
 */
export interface IsGpuUtilizationPreferenceSupportedReturn {
    isSupported: boolean;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GetGpuUtilizationPreferenceInfoReturn".
 */
export interface GetGpuUtilizationPreferenceInfoReturn {
    gpuUtilizationPreferenceInfo: GpuUtilizationPreferenceInfo;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetGpuUtilizationPreferenceParameters".
 */
export interface SetGpuUtilizationPreferenceParameters {
    /**
     * GPU utilization YieldPct requested by NvApp.
     */
    yieldPct: number;
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SetGpuUtilizationPreferenceReturn".
 */
export interface SetGpuUtilizationPreferenceReturn {
    writeSucceeded: boolean;
    errorCode: GpuUtilizationPreferenceErrorCode;
}

/**
 * Enum channel of the update
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "RAMType".
 */
export enum RamType {
    UNKNOWN = 0,
    SDRAM = 1,
    DDR1 = 2,
    DDR2 = 3,
    GDDR2 = 4,
    GDDR3 = 5,
    GDDR4 = 6,
    DDR3 = 7,
    GDDR5 = 8,
    GDDR5X = 9,
    HBM1 = 10,
    HBM2 = 11,
    SDDR4 = 12,
    GDDR6 = 14,
    GDDR6X = 15,
    GDDR7 = 16
}
/**
 * Enum channel of the update
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "BusType".
 */
export enum BusType {
    UNKNOWN = 0,
    AGP = 1,
    PCI = 2,
    FPCI = 3,
    PCI_EXPRESS = 4,
    PCI_EXPRESS_GEN2 = 5,
    PCI_EXPRESS_GEN3 = 6,
    PCI_EXPRESS_GEN4 = 7,
    PCI_EXPRESS_GEN5 = 8,
    PCI_EXPRESS_GEN6 = 9,
    AXI = 10
}
/**
 * Enum MaxQ type
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "MaxQType".
 */
export enum MaxQType {
    No = 0,
    On = 1,
    Yes = 2
}
/**
 * DDS KMD Response
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "KmdResponse".
 */
export enum KmdResponse {
    Response_Ok = 0,
    Response_MultiHead = 1,
    Response_MultiHeadInactive = 2,
    Response_IGpuUnavailable = 3,
    Response_IGpuInternalHeadUnavailable = 4,
    Response_IGpuInternalHeadRecovered = 5,
    Response_ConnectedDisplay = 6
}
/**
 * Type of self refresh technology detected
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SRType".
 */
export enum SrType {
    PSR_Unknown = 0,
    Sideband = 1,
    PSR_Supported = 2,
    PSR_Failed = 3
}
/**
 * IGPU or DGPU
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "MuxState".
 */
export enum MuxState {
    State_Invalid = 0,
    State_IGPU = 1,
    State_DGPU = 2
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "MosaicType".
 */
export enum MosaicType {
    MOSAIC_NONE = 0,
    SLIMOSAIC = 1,
    SURROUND = 2,
    BASE_MOSAIC = 3,
    NEXTGENSURROUND = 4
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "RotationType".
 */
export enum RotationType {
    Rotation0 = 0,
    Rotation90 = 1,
    Rotation180 = 2,
    Rotation270 = 3
}
/**
 * ECC Mode Return Value
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ECCModeResult".
 */
export enum EccModeResult {
    ECCMODE_SUCCESS = 0,
    ECCMODE_FAIL = 1
}
/**
 * Super Resolution Value
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SuperResolutionValue".
 */
export enum SuperResolutionValue {
    SuperResValue_Off = 0,
    SuperResValue_1 = 1,
    SuperResValue_2 = 2,
    SuperResValue_3 = 3,
    SuperResValue_4 = 4,
    SuperResValue_Auto = 5
}
/**
 * Enum MaxQ type
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SuperResolutionStatus".
 */
export enum SuperResolutionStatus {
    SuperResStatus_Off = 0,
    SuperResStatus_1 = 1,
    SuperResStatus_2 = 2,
    SuperResStatus_3 = 3,
    SuperResStatus_4 = 4
}
/**
 * Preferred GPU utilization
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SuperResolutionGpuUtilization".
 */
export enum SuperResolutionGpuUtilization {
    SuperResGpuUtil_Low = 0,
    SuperResGpuUtil_Medium = 1,
    SuperResGpuUtil_High = 2
}
/**
 * VSR indicator status
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "SuperResolutionIndicator".
 */
export enum SuperResolutionIndicator {
    SuperResIndicator_Off = 0,
    SuperResIndicator_On = 1
}
/**
 * Indicates what to do with VSR when game is launched
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "DisableVSROnGameLaunch".
 */
export enum DisableVsrOnGameLaunch {
    DisableVSROnGameLaunch_Off = 0,
    DisableVSROnGameLaunch_On = 1
}
/**
 * HDR indicator status
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "HDRIndicator".
 */
export enum HdrIndicator {
    HDRIndicator_Off = 0,
    HDRIndicator_On = 1
}
/**
 * Indicates what to do with HDR when game is launched
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "DisableHDROnGameLaunch".
 */
export enum DisableHdrOnGameLaunch {
    DisableHDROnGameLaunch_Off = 0,
    DisableHDROnGameLaunch_On = 1
}
/**
 * Enum MaxQ type
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "HDRErrorCodeInfo".
 */
export enum HdrErrorCodeInfo {
    SUCCESS = 0,
    NO_HDR_DISPLAY_CONNECTED = 1,
    ENABLE_HDR_FROM_WINDOWS = 2,
    SURROUND_ENABLED = 3
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "OutPutColorFormat".
 */
export enum OutPutColorFormat {
    RGB = 0,
    YCBCR422 = 1,
    YCBCR444 = 2,
    YCBCR420 = 3,
    UNKNOWN = 4
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GSyncCapabilities".
 */
export enum GSyncCapabilities {
    GsyncUltimate = 0,
    GSyncCapable = 1,
    GSyncCompatible = 2,
    VRR = 3,
    NotSupported = 4,
    BasicVRR = 5
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GlobalGsyncState".
 */
export enum GlobalGsyncState {
    Off = 0,
    FullscreenOnly = 1,
    FullscreenAndWindowed = 2,
    NotAvailable = 3
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "TvFormat".
 */
export enum TvFormat {
    None = 0,
    NonHD = 1,
    SD576i = 2,
    SD480i = 3,
    ED480p = 4,
    ED576p = 5,
    HD720p = 6,
    HD1080i = 7,
    HD1080p = 8,
    HD720p50 = 9,
    HD1080p24 = 10,
    HD1080i50 = 11,
    HD1080p50 = 12,
    UHD4Kp30 = 13,
    UHD4Kp25 = 14,
    UHD4Kp24 = 15,
    UHD4Kp24Smpte = 16,
    UHD4Kp303840 = 17,
    UHD4Kp253840 = 18,
    UHD4Kp243840 = 19,
    UHD4Kp503840 = 20,
    UHD4Kp603840 = 21,
    UHD4Kp304096 = 22,
    UHD4Kp254096 = 23,
    UHD4Kp244096 = 24,
    UHD4Kp504096 = 25,
    UHD4Kp604096 = 26,
    UHD8Kp247680 = 27,
    UHD8Kp257680 = 28,
    UHD8Kp307680 = 29,
    UHD8Kp487680 = 30,
    UHD8Kp507680 = 31,
    UHD8Kp607680 = 32,
    UHD8Kp1007680 = 33,
    UHD8Kp1207680 = 34,
    Invalid = 35
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ConnectorType".
 */
export enum ConnectorType {
    CT_VGA = 0,
    CT_DVI = 1,
    CT_COMPOSITE = 2,
    CT_SVIDEO = 3,
    CT_COMPONENT = 4,
    CT_HDMI = 5,
    CT_DISPLAYPORT = 6,
    CT_USBC = 7,
    CT_LVDS = 8,
    CT_AUTO = 9,
    CT_UNKNOWN = 10
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "DesktopColorDepth".
 */
export enum DesktopColorDepth {
    bit_8 = 0,
    bit_10 = 1,
    bit_16 = 2,
    bit_16_HDR = 3,
    bit_16_WCG = 4,
    bit_Default = -1
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "DeviceType".
 */
export enum DeviceType {
    None = 0,
    Crt = 1,
    Internal = 2,
    Tv = 3,
    Dvi = 4
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "LogicalDisplayType".
 */
export enum LogicalDisplayType {
    None = 0,
    CloneDisplay = 1,
    SurroundDisplay = 2,
    MosaicDisplay = 3
}
/**
 * Output dynamic range
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "DynamicRange".
 */
export enum DynamicRange {
    Invalid = -1,
    VESA = 0,
    CEA = 1,
    AUTO = 2
}
/**
 * Output color depth
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "OutputColorDepth".
 */
export enum OutputColorDepth {
    Invalid = -1,
    BPC_6 = 0,
    BPC_8 = 1,
    BPC_10 = 2,
    BPC_12 = 3,
    BPC_16 = 4
}
/**
 * Color accuracy mode
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ColorAccuracyMode".
 */
export enum ColorAccuracyMode {
    Invalid = 0,
    Auto = 1,
    Reference = 2,
    Accurate = 3,
    Enhanced = 4
}
/**
 * enum for timing formula
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "TimingFormula".
 */
export enum TimingFormula {
    Auto = 0,
    GTF = 1,
    DMT = 2,
    CVT = 3,
    CVTRB = 4,
    Manual = 5,
    OverrideEIA861 = 6
}
/**
 * enum for Scaling types
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ScalingType".
 */
export enum ScalingType {
    Monitor = 0,
    Adapter = 1,
    Centered = 2,
    FixedAspect = 3
}
/**
 * enum for Scalinng methods
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ScalingMethod".
 */
export enum ScalingMethod {
    None = 0,
    FullScreen = 1,
    FixedAspect = 2,
    NoScale = 3,
    IntegerScaling = 4
}
/**
 * enum for Scaling mode
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ScalingMode".
 */
export enum ScalingMode {
    None = 0,
    Gpu = 1,
    Display = 2
}
/**
 * Custom resolution operation flags matching NVCPLAPI_ENUM_CT_OP
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "CustomResolutionOperation".
 */
export enum CustomResolutionOperation {
    Set = 1,
    Revert = 2,
    Save = 3
}
/**
 * Possible actions for local Custom resolution cache
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "CustomResolutionLocalCacheActions".
 */
export enum CustomResolutionLocalCacheActions {
    ClearCache = 0,
    RefreshCache = 1,
    AddResolution = 2,
    FlushToStorage = 3
}
/**
 * Content types reported to display
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "ContentType".
 */
export enum ContentType {
    Auto = 0,
    Video = 1,
    Desktop = 2,
    Photo = 3,
    Movie = 4,
    Game = 5
}
/**
 * UI Element which we are referring to
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "UIElemType".
 */
export enum UiElemType {
    DDSTrayIcon = 0,
    DDSDesktopNotification = 1,
    SurroundConfineTaskbarToSingleDisplay = 2,
    SurroundMaximizeWindowsAcrossAllDisplays = 3
}
/**
 * UI Element state
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "UIElemState".
 */
export enum UiElemState {
    DISABLE = 0,
    ENABLE = 1
}
/**
 * Types of hotkeys supported
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "HotkeyType".
 */
export enum HotkeyType {
    ToggleSurround = 0,
    ToggleSurroundResolution = 1,
    BezelPeeking = 2
}
/**
 * Native GPU utilization YieldPct preference value.
 *
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GpuUtilizationPreferenceValue".
 */
export enum GpuUtilizationPreferenceValue {
    NoPriority = 0,
    ForegroundAppPriority = 10,
    Balanced = 20,
    BackgroundAppPriority = 50
}
/**
 * This interface was referenced by `NvCplDisplayplugin`'s JSON-Schema
 * via the `definition` "GpuUtilizationPreferenceErrorCode".
 */
export enum GpuUtilizationPreferenceErrorCode {
    None = 'None',
    NotSupported = 'NotSupported',
    InvalidArgument = 'InvalidArgument',
    AuthDenied = 'AuthDenied',
    WriteFailed = 'WriteFailed'
}
export enum ErrorCode {
    None = 'None',
    AuthDenied = 'AuthDenied',
    RestartRejected = 'RestartRejected',
    WriteFailed = 'WriteFailed'
}
export enum ErrorCode1 {
    None = 'None',
    AuthDenied = 'AuthDenied',
    RestartRejected = 'RestartRejected',
    WriteFailed = 'WriteFailed'
}
export enum ErrorCode2 {
    None = 'None',
    NotSupported = 'NotSupported',
    AuthDenied = 'AuthDenied',
    RestartRejected = 'RestartRejected',
    WriteFailed = 'WriteFailed'
}


}
